import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ToastService } from '@core/services/toast.service';
import { ServiceWorkerService } from '@core/services/serviceworker.service';
import { interval, Subscription } from 'rxjs';
import { SharedModule } from '@shared/shared.module';

@Component({
  selector: 'app-update-notification',
  standalone: true,
  imports: [SharedModule],
  templateUrl: './update-notification.component.html',
  styleUrls: ['./update-notification.component.scss'],
})
export class UpdateNotificationComponent implements OnInit {
  readonly showUpdateNotification = signal(false);
  readonly countdown = signal(20);

  private readonly toast = inject(ToastService);
  private readonly serviceWorkerService = inject(ServiceWorkerService);
  private readonly destroyRef = inject(DestroyRef);
  private countdownSub: Subscription | null = null;

  ngOnInit(): void {
    this.toast.messages$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((message: unknown) => {
      if (Array.isArray(message)) {
        const updateMessage = message.find(
          (m) => m != null && typeof m === 'object' && 'key' in m && (m as { key?: string }).key === 'update-available'
        );
        if (updateMessage) {
          this.showUpdateNotification.set(true);
          this.startCountdown();
        }
      } else if (
        message &&
        typeof message === 'object' &&
        'key' in message &&
        (message as { key?: string }).key === 'update-available'
      ) {
        this.showUpdateNotification.set(true);
        this.startCountdown();
      }
    });
  }

  updateNow(): void {
    this.showUpdateNotification.set(false);
    this.toast.clear('update-available');
    this.serviceWorkerService.refresh();
  }

  dismissNotification(): void {
    this.showUpdateNotification.set(false);
    this.toast.clear('update-available');
    this.countdownSub?.unsubscribe();
    this.remindUserLater(60);
  }

  private startCountdown(): void {
    this.countdownSub?.unsubscribe();
    this.countdown.set(20);

    const countdown$ = interval(1000).pipe(takeUntilDestroyed(this.destroyRef));
    this.countdownSub = countdown$.subscribe(() => {
      const current = this.countdown();
      if (current > 1) {
        this.countdown.set(current - 1);
      } else {
        this.countdownSub?.unsubscribe();
        this.updateNow();
      }
    });
  }

  private remindUserLater(seconds: number): void {
    setTimeout(() => {
      this.showUpdateNotification.set(true);
      this.startCountdown();
    }, seconds * 1000);
  }
}
